import {
  bytesToHex,
  decodeApiVersion,
  decodeEvent,
  encodeCommand,
  encodeParSetup,
  encodeUnixTime,
  SG_CHAR,
  SG_TIMER_NAME_PREFIX,
  SG_TIMER_SERVICE_UUID,
  SgCommandId,
  type SgEvent,
  type SgInput,
  type TimerListener,
  type TimerShotEvent,
  type TimerSource,
  type TimerStatus,
} from '@flm9/scoring';

export function bleAvailable(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof (navigator as Navigator & { bluetooth?: unknown }).bluetooth === 'object'
  );
}

export interface RawEventLogEntry {
  /** Wall-clock when the notification arrived (Date.now()). */
  at: number;
  /** Direction: notification (← timer) or write (→ timer). */
  dir: 'in' | 'out';
  /** Which characteristic the bytes belong to. */
  char: 'event' | 'command' | 'par' | 'time';
  /** Hex-formatted payload. */
  hex: string;
  /** Decoded event, when applicable. */
  decoded?: SgEvent | string;
}

/** PAR_SETUP start_delay value for "random 1.0-4.0 s" (SG protocol). */
export const RANDOM_START_DELAY_DS = 0xffff;

export interface SgTimerOptions {
  /**
   * When provided, shots beyond `expectedShots` are silently dropped and
   * the source auto-stops the device once `shot_num === expectedShots`.
   * Pass `null` (the default) for variable-shot disciplines like IPSC —
   * the caller must explicitly call `.stop()` to end timing.
   */
  expectedShots?: number | null;
  /** PAR_SETUP start_delay in 0.1 s units. 0xFFFF = random 1-4 s. Default 30 (3 s). */
  parStartDelayDS?: number;
  /** PAR_SETUP time_limit in 0.1 s units. 0 = unlimited. Default 0. */
  parTimeLimitDS?: number;
  /** Cap on raw event-log size; older entries fall off the front. */
  rawLogLimit?: number;
}

/**
 * SG Timer (Sport / GO) source over Web Bluetooth.
 *
 * Pairing is split from session control: `connect()` talks to the GATT
 * server and subscribes to notifications; `start()` writes PAR config
 * and `SESSION_START` to the device. The timer's own SESSION_SET_BEGIN
 * event (after the start delay) is what flips us to `'running'` so the
 * `tFromStartMs` we surface matches the start beep the shooter hears.
 */
export class SgTimerSource implements TimerSource {
  readonly id = 'sg';
  name = 'SG Timer';

  private device: BluetoothDevice | null = null;
  private commandChar: BluetoothRemoteGATTCharacteristic | null = null;
  private eventChar: BluetoothRemoteGATTCharacteristic | null = null;
  private parChar: BluetoothRemoteGATTCharacteristic | null = null;
  private timeChar: BluetoothRemoteGATTCharacteristic | null = null;
  private apiChar: BluetoothRemoteGATTCharacteristic | null = null;

  private _status: TimerStatus = { state: 'idle', startedAt: null, shots: [] };
  private listeners = new Set<TimerListener>();

  /** True once a connection is established/desired; cleared by an explicit
   *  user `disconnect()`. Gates auto-reconnect so a user-initiated close
   *  never loops. */
  private shouldReconnect = false;
  /** In-flight guard: a connect/reconnect attempt is currently running. */
  private reconnecting = false;
  /** Pending backoff tick handle (window.setTimeout id), or null. */
  private reconnectTimer: number | null = null;
  /** Backoff exponent; reset to 0 on success / fresh foreground kick. */
  private reconnectAttempt = 0;
  private static readonly BACKOFF_BASE_MS = 1000;
  private static readonly BACKOFF_MAX_MS = 30000;
  private rawLog: RawEventLogEntry[] = [];
  private rawLogListeners = new Set<(log: readonly RawEventLogEntry[]) => void>();

  private opts: Required<
    Pick<SgTimerOptions, 'parStartDelayDS' | 'parTimeLimitDS' | 'rawLogLimit'>
  > & {
    expectedShots: number | null;
  };

  constructor(opts: SgTimerOptions = {}) {
    this.opts = {
      expectedShots: opts.expectedShots ?? null,
      parStartDelayDS: opts.parStartDelayDS ?? 30,
      parTimeLimitDS: opts.parTimeLimitDS ?? 0,
      rawLogLimit: opts.rawLogLimit ?? 100,
    };
  }

  setExpectedShots(n: number | null): void {
    this.opts.expectedShots = n;
  }
  setPar(startDelayDS: number, timeLimitDS: number): void {
    this.opts.parStartDelayDS = startDelayDS;
    this.opts.parTimeLimitDS = timeLimitDS;
  }
  /** Push only the time_limit, leaving start_delay untouched. Used by the
   *  Score screen to align the device's PAR with the active drill's
   *  configured maximum time without disturbing the club's persisted
   *  start-delay default. */
  setTimeLimit(timeLimitDS: number): void {
    this.opts.parTimeLimitDS = timeLimitDS;
  }
  /** Read-only accessor used by the Score screen so it can capture the
   *  pre-run value and restore it after the drill unmounts. */
  getTimeLimit(): number {
    return this.opts.parTimeLimitDS;
  }

  isConnected(): boolean {
    return this.device?.gatt?.connected === true;
  }

  rawEventLog(): readonly RawEventLogEntry[] {
    return this.rawLog;
  }

  onRawLog(listener: (log: readonly RawEventLogEntry[]) => void): () => void {
    this.rawLogListeners.add(listener);
    listener(this.rawLog);
    return () => this.rawLogListeners.delete(listener);
  }

  /**
   * Open a Chrome chooser, pair the device, subscribe to EVENT
   * notifications, sync the timer's clock and read API_VERSION. Idempotent
   * — calling on an already-connected device is a no-op apart from
   * re-attaching listeners.
   */
  async connect(): Promise<void> {
    if (!bleAvailable()) throw new Error('web_bluetooth_unavailable');
    const nav = navigator as Navigator & { bluetooth: Bluetooth };

    // A successful manual connect cancels any pending backoff and (re)arms
    // the auto-reconnect desire — clearing any prior user-initiated close.
    this.cancelReconnect();
    this.shouldReconnect = true;

    if (!this.device) {
      this.device = await nav.bluetooth.requestDevice({
        filters: [{ namePrefix: SG_TIMER_NAME_PREFIX }],
        optionalServices: [SG_TIMER_SERVICE_UUID],
      });
      this.device.addEventListener('gattserverdisconnected', this.onDisconnected);
    }
    if (!this.device.gatt) throw new Error('gatt_missing');

    const server = this.device.gatt.connected ? this.device.gatt : await this.device.gatt.connect();
    const apiVersion = await this.discoverAndSubscribe(server);

    this.reconnectAttempt = 0;
    const next: TimerStatus = { ...this._status, disconnected: false, reconnecting: false };
    if (this.device.name) next.deviceName = this.device.name;
    if (apiVersion) next.apiVersion = apiVersion;
    this._status = next;
    this.emit();
  }

  /**
   * Discover the SG service characteristics on a freshly-connected GATT
   * server, subscribe to EVENT + COMMAND notifications, then best-effort
   * read API_VERSION and sync the clock. Shared by `connect()` and the
   * reconnect / adopt paths. Every GATT (re)connect yields brand-new
   * characteristic instances, so re-attaching the value-changed listeners
   * here can never double-subscribe. Returns the decoded API version when
   * available.
   */
  private async discoverAndSubscribe(
    server: BluetoothRemoteGATTServer,
  ): Promise<string | undefined> {
    const service = await server.getPrimaryService(SG_TIMER_SERVICE_UUID);

    this.commandChar = await service.getCharacteristic(SG_CHAR.command);
    this.eventChar = await service.getCharacteristic(SG_CHAR.event);
    this.parChar = await service.getCharacteristic(SG_CHAR.parSetup);
    this.timeChar = await service.getCharacteristic(SG_CHAR.unixTime);
    this.apiChar = await service.getCharacteristic(SG_CHAR.apiVersion);

    this.eventChar.addEventListener('characteristicvaluechanged', this.onEventNotification);
    await this.eventChar.startNotifications();

    this.commandChar.addEventListener('characteristicvaluechanged', this.onCommandResponse);
    await this.commandChar.startNotifications();

    let apiVersion: string | undefined;
    try {
      const apiBuf = await this.apiChar.readValue();
      apiVersion = decodeApiVersion(apiBuf);
    } catch {
      // Some firmwares deny early reads — non-fatal.
    }

    try {
      await this.timeChar.writeValueWithResponse(encodeUnixTime(Math.floor(Date.now() / 1000)));
    } catch {
      // Non-fatal — clock sync is best-effort.
    }

    return apiVersion;
  }

  /**
   * Adopt a previously-granted device after a page reload and connect
   * silently (no chooser, no user gesture). No-op when `getDevices()` is
   * unavailable (non-Chromium) or no granted device matches. If the device
   * is granted but currently off / out of range, arms the backoff loop so
   * it reconnects once it reappears. Returns true on an immediate connect.
   */
  async tryAdoptKnownDevice(): Promise<boolean> {
    if (this.device) return false; // already paired this tab
    if (!bleAvailable()) return false;
    const bt = (
      navigator as Navigator & {
        bluetooth?: Bluetooth & { getDevices?: () => Promise<BluetoothDevice[]> };
      }
    ).bluetooth;
    if (!bt || typeof bt.getDevices !== 'function') return false; // Chromium-only

    let devices: BluetoothDevice[] = [];
    try {
      devices = await bt.getDevices();
    } catch {
      return false;
    }
    // Prefer a named SG timer; fall back only to a granted device with no
    // cached name (could still be the timer), never to a differently-named one.
    const match =
      devices.find((d) => d.name?.startsWith(SG_TIMER_NAME_PREFIX)) ??
      devices.find((d) => !d.name);
    if (!match || !match.gatt) return false;
    const gatt = match.gatt;

    this.device = match;
    this.device.addEventListener('gattserverdisconnected', this.onDisconnected);
    this.shouldReconnect = true;
    try {
      const server = await gatt.connect();
      const apiVersion = await this.discoverAndSubscribe(server);
      this.reconnectAttempt = 0;
      const next: TimerStatus = { ...this._status, disconnected: false, reconnecting: false };
      if (this.device.name) next.deviceName = this.device.name;
      if (apiVersion) next.apiVersion = apiVersion;
      this._status = next;
      this.emit();
      return true;
    } catch {
      // Granted but unreachable right now — show amber and let the backoff
      // loop pick it up when it comes back into range / powers on.
      this._status = { ...this._status, disconnected: true, reconnecting: true };
      this.emit();
      this.scheduleReconnect();
      return false;
    }
  }

  async disconnect(): Promise<void> {
    // User-initiated close: never auto-reconnect, and kill any pending
    // backoff. `this.device` + the gattserverdisconnected listener are kept
    // so a later manual connect() stays gesture-free.
    this.shouldReconnect = false;
    this.cancelReconnect();
    if (this.eventChar) {
      try {
        await this.eventChar.stopNotifications();
      } catch {
        // Ignore — the device may already be gone.
      }
      this.eventChar.removeEventListener('characteristicvaluechanged', this.onEventNotification);
    }
    if (this.commandChar) {
      try {
        await this.commandChar.stopNotifications();
      } catch {
        // Ignore.
      }
      this.commandChar.removeEventListener('characteristicvaluechanged', this.onCommandResponse);
    }
    if (this.device?.gatt?.connected) this.device.gatt.disconnect();
    this.commandChar = this.eventChar = this.parChar = this.timeChar = this.apiChar = null;
  }

  // --- TimerSource contract ------------------------------------------------

  /**
   * Start a session. Writes PAR_SETUP first (start_delay/time_limit/
   * shot_limit derived from this.opts) so the device enforces the same
   * shot cap we enforce in `onEventNotification`.
   */
  async start(opts: { startDelayDS?: number } = {}): Promise<void> {
    if (!this.commandChar || !this.parChar) throw new Error('not_connected');
    const shotLimit = this.opts.expectedShots ?? 0;
    // `startDelayDS` overrides the configured delay for this run only
    // (Standby uses RANDOM_START_DELAY_DS); the stored default is untouched.
    const par = encodeParSetup(
      opts.startDelayDS ?? this.opts.parStartDelayDS,
      this.opts.parTimeLimitDS,
      shotLimit,
    );
    this.appendRaw('out', 'par', par);
    await this.parChar.writeValueWithResponse(par);
    const cmd = encodeCommand(SgCommandId.SESSION_START);
    this.appendRaw('out', 'command', cmd);
    await this.commandChar.writeValueWithResponse(cmd);
    this._status = { ...this._status, state: 'armed', startedAt: null, shots: [] };
    this.emit();
  }

  async stop(): Promise<void> {
    if (this.commandChar) {
      const cmd = encodeCommand(SgCommandId.SESSION_STOP);
      this.appendRaw('out', 'command', cmd);
      try {
        await this.commandChar.writeValueWithResponse(cmd);
      } catch {
        // Already stopped or out of range.
      }
    }
    this._status = { ...this._status, state: 'stopped' };
    this.emit();
  }

  async suspend(): Promise<void> {
    if (!this.commandChar) return;
    const cmd = encodeCommand(SgCommandId.SESSION_SUSPEND);
    this.appendRaw('out', 'command', cmd);
    await this.commandChar.writeValueWithResponse(cmd);
  }

  async resume(): Promise<void> {
    if (!this.commandChar) return;
    const cmd = encodeCommand(SgCommandId.SESSION_RESUME);
    this.appendRaw('out', 'command', cmd);
    await this.commandChar.writeValueWithResponse(cmd);
  }

  reset(): void {
    const { lastSessId: _drop, ...rest } = this._status;
    this._status = { ...rest, state: 'idle', startedAt: null, shots: [] };
    this.emit();
  }

  on(listener: TimerListener): () => void {
    this.listeners.add(listener);
    listener(this._status);
    return () => this.listeners.delete(listener);
  }

  status(): TimerStatus {
    return this._status;
  }

  // --- internals -----------------------------------------------------------

  private onEventNotification = (e: Event): void => {
    const target = e.target as BluetoothRemoteGATTCharacteristic;
    const dv = target.value;
    if (!dv) return;
    const decoded = decodeEvent(dv);
    this.appendRaw('in', 'event', dv, decoded);
    if (!decoded) return;

    switch (decoded.kind) {
      case 'session_started':
        this._status = {
          ...this._status,
          state: 'armed',
          startedAt: null,
          shots: [],
          lastSessId: decoded.sessId,
        };
        this.emit();
        break;
      case 'session_set_begin':
        this._status = {
          ...this._status,
          state: 'running',
          startedAt: Date.now(),
          lastSessId: decoded.sessId,
        };
        this.emit();
        break;
      case 'shot_detected': {
        // Overflow shots — beyond the configured shotCount — are dropped
        // entirely per the auto-timing contract. Raw bytes are still in
        // the debug log so we don't lose the audit trail.
        if (
          this.opts.expectedShots != null &&
          this.opts.expectedShots > 0 &&
          decoded.shotNum > this.opts.expectedShots
        ) {
          break;
        }
        const last = this._status.shots[this._status.shots.length - 1];
        // Duplicate / re-delivered notification: shot numbers only grow
        // within a session (`session_started` clears the list).
        if (last && decoded.shotNum <= last.ord) break;
        const ev: TimerShotEvent = {
          ord: decoded.shotNum,
          tFromStartMs: decoded.shotTimeMs,
          ...(last ? { splitMs: decoded.shotTimeMs - last.tFromStartMs } : {}),
        };
        this._status = { ...this._status, shots: [...this._status.shots, ev] };
        this.emit();
        if (
          this.opts.expectedShots != null &&
          this.opts.expectedShots > 0 &&
          decoded.shotNum >= this.opts.expectedShots
        ) {
          // Tell the device to wrap up too — defense in depth, in case
          // the firmware-level shot_limit didn't catch it.
          void this.stop();
        }
        break;
      }
      case 'session_suspended':
      case 'session_resumed':
        this._status = {
          ...this._status,
          state: decoded.kind === 'session_suspended' ? 'idle' : 'running',
          lastSessId: decoded.sessId,
        };
        this.emit();
        break;
      case 'session_stopped':
        this._status = {
          ...this._status,
          state: 'stopped',
          lastSessId: decoded.sessId,
        };
        this.emit();
        break;
    }
  };

  private onCommandResponse = (e: Event): void => {
    const target = e.target as BluetoothRemoteGATTCharacteristic;
    const dv = target.value;
    if (!dv) return;
    this.appendRaw('in', 'command', dv);
  };

  private onDisconnected = (): void => {
    // Stale after a GATT drop — re-fetched fresh on the next (re)connect.
    this.commandChar = this.eventChar = this.parChar = this.timeChar = this.apiChar = null;
    if (!this.shouldReconnect) {
      // User-initiated or never-desired: red, no retry.
      this._status = { ...this._status, disconnected: true, reconnecting: false };
      this.emit();
      return;
    }
    // Unexpected drop: amber, begin backoff.
    this._status = { ...this._status, disconnected: true, reconnecting: true };
    this.emit();
    this.scheduleReconnect();
  };

  /** Schedule one backoff tick. No-ops if a tick is already pending, an
   *  attempt is in flight, or reconnection is no longer desired. */
  private scheduleReconnect(): void {
    if (!this.shouldReconnect) return;
    if (this.reconnectTimer != null) return;
    if (this.reconnecting) return;
    const delay = Math.min(
      SgTimerSource.BACKOFF_MAX_MS,
      SgTimerSource.BACKOFF_BASE_MS * 2 ** this.reconnectAttempt,
    );
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      void this.attemptReconnect();
    }, delay);
  }

  /** Attempt a single gesture-free reconnect on the retained device. On
   *  failure, grows the backoff and reschedules. */
  private async attemptReconnect(): Promise<void> {
    if (!this.shouldReconnect || !this.device?.gatt) return;
    if (this.reconnecting) return; // in-flight guard
    if (this.device.gatt.connected) {
      // Already back (e.g. a visibility kick raced the backoff tick).
      this.onReconnectSuccess();
      return;
    }
    this.reconnecting = true;
    try {
      const server = await this.device.gatt.connect(); // no requestDevice, no gesture
      await this.discoverAndSubscribe(server);
      this.reconnecting = false;
      this.onReconnectSuccess();
    } catch {
      this.reconnectAttempt += 1;
      this.reconnecting = false;
      this.scheduleReconnect();
    }
  }

  private onReconnectSuccess(): void {
    this.cancelReconnect();
    this.reconnecting = false;
    const next: TimerStatus = { ...this._status, disconnected: false, reconnecting: false };
    if (this.device?.name) next.deviceName = this.device.name;
    this._status = next;
    this.emit();
  }

  /** Clear any pending backoff tick and reset the backoff counter. */
  private cancelReconnect(): void {
    if (this.reconnectTimer != null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempt = 0;
  }

  /** Idempotent nudge: if a connection is desired but down and nothing is
   *  in flight, retry immediately (resetting backoff). Safe to call from a
   *  visibilitychange listener when the PWA returns to the foreground. */
  maybeReconnectNow(): void {
    if (!this.shouldReconnect) return;
    if (this.reconnecting) return;
    if (this.device?.gatt?.connected) return;
    this.cancelReconnect(); // reset backoff so a foreground return retries instantly
    void this.attemptReconnect();
  }

  private appendRaw(
    dir: 'in' | 'out',
    char: RawEventLogEntry['char'],
    buf: SgInput,
    decoded?: SgEvent | null,
  ): void {
    this.rawLog = [
      ...this.rawLog.slice(-(this.opts.rawLogLimit - 1)),
      {
        at: Date.now(),
        dir,
        char,
        hex: bytesToHex(buf),
        ...(decoded ? { decoded } : {}),
      },
    ];
    for (const l of this.rawLogListeners) l(this.rawLog);
  }

  private emit(): void {
    for (const l of this.listeners) l(this._status);
  }
}
